import { format } from "date-fns";
import {
  toPaymentStatus,
  toProductType,
} from "features/purchaseHistory/purchaseHistoryOrderValues";

import type {
  LegacyPortalPurchaseHistoryOrder,
  LegacyPortalPurchaseHistoryOrderItem,
  LegacyPortalPurchaseHistoryUnpaidPaymentInfo,
} from "@spwn-portal/grpc";
import type { CVSCode, PhoneCode } from "@spwn/types/gmo";
import type {
  ConfirmCvsPaymentCvsInfo,
  ConfirmCvsPaymentEventGroup,
  ConfirmCvsPaymentItem,
  ConfirmCvsPaymentPayPalInfo,
  ConfirmCvsPaymentPhoneInfo,
  Props as ConfirmCvsPaymentViewProps,
} from "features/confirmCvsPayment/confirmCvsPaymentViewProps";
import type { useTranslation } from "react-i18next";

/** 支払案内モーダルに渡す値。開閉と閉じる操作は呼び出し側が持つ */
type ConfirmCvsPaymentOrderProps = Omit<
  ConfirmCvsPaymentViewProps,
  "close" | "isOpen"
>;

type Input = {
  order: LegacyPortalPurchaseHistoryOrder;
  t: ReturnType<typeof useTranslation>["t"];
};

const CVS_CODES: Record<string, CVSCode | undefined> = {
  "10001": "10001",
  "10002": "10002",
  "10008": "10008",
};

const PHONE_CODES: Record<string, PhoneCode | undefined> = {
  au: "au",
  docomo: "docomo",
  dpay: "dpay",
  sb: "sb",
};

const getItemVariantName = (
  item: LegacyPortalPurchaseHistoryOrderItem
): string => {
  const { detail } = item;
  if (detail.case === "spwn") return detail.value.subClassName ?? "";
  if (detail.case === "shopify") return detail.value.variantTitle ?? "";
  return "";
};

const toItems = (
  items: LegacyPortalPurchaseHistoryOrderItem[]
): ConfirmCvsPaymentItem[] =>
  items.map((item) => ({
    count: item.quantity,
    name: item.name,
    price: item.price,
    variantName: getItemVariantName(item),
  }));

const toCvsInfo = (
  payType: string | undefined,
  info: LegacyPortalPurchaseHistoryUnpaidPaymentInfo | undefined
): ConfirmCvsPaymentCvsInfo | null => {
  if (payType !== "CVS" || info === undefined) return null;
  const cvsCode = CVS_CODES[info.convenience ?? ""];
  if (cvsCode === undefined) return null;
  return { confNo: info.confNo, cvsCode, receiptNo: info.receiptNo };
};

const toPhoneInfo = (
  payType: string | undefined,
  info: LegacyPortalPurchaseHistoryUnpaidPaymentInfo | undefined
): ConfirmCvsPaymentPhoneInfo | null => {
  if (payType !== "Phone" || info === undefined) return null;
  const phoneCode = PHONE_CODES[info.phoneCode ?? ""];
  if (phoneCode === undefined) return null;
  return { phoneCode, phoneUrl: info.phoneUrl };
};

const toPayPalInfo = (
  payType: string | undefined,
  info: LegacyPortalPurchaseHistoryUnpaidPaymentInfo | undefined
): ConfirmCvsPaymentPayPalInfo | null => {
  // 支払URLが無いと案内できない（リンク先の無い支払案内を出さない。Firestore直読みの経路と同じ扱い）
  if (payType !== "PayPal" || info?.paypalUrl === undefined) return null;
  return { paypalUrl: info.paypalUrl };
};

const formatDateFull = (millis: number): string =>
  format(new Date(millis), "yyyy/MM/dd");

/**
 * イベント紐付きの明細をイベントごとにまとめ、見出し（イベント名・開催日）を明細から再構成する。
 * 出演者名と会場名は購入履歴APIに無いため出さない。
 * イベントに紐づかない明細が1つでもあれば見出しなしの一覧にする
 */
const toEventGroups = (
  order: LegacyPortalPurchaseHistoryOrder,
  t: Input["t"]
): ConfirmCvsPaymentEventGroup[] | null => {
  if (order.items.some((item) => item.eid === undefined)) return null;

  const itemsByEvent = new Map<
    string,
    LegacyPortalPurchaseHistoryOrderItem[]
  >();
  order.items.forEach((item) => {
    const eid = item.eid ?? "";
    itemsByEvent.set(eid, [...(itemsByEvent.get(eid) ?? []), item]);
  });

  return Array.from(itemsByEvent.values()).map((items) => {
    const [firstItem] = items;
    const openTimes = (firstItem?.event?.parts ?? []).map((part) =>
      Number(part.openTime)
    );
    const eventOpenMillis =
      openTimes.length > 0 ? Math.min(...openTimes) : null;
    const eventEndMillis = openTimes.length > 0 ? Math.max(...openTimes) : null;
    const eventOpenDateFull =
      eventOpenMillis === null ? "" : formatDateFull(eventOpenMillis);
    const eventEndDateFull =
      eventEndMillis === null ? "" : formatDateFull(eventEndMillis);
    // 見出しはマイチケット（イベントドキュメントのtitle）と揃えるため、長い表記を優先する
    const eventTitle =
      firstItem?.detail.case === "spwn"
        ? (firstItem.detail.value.eventLongTitle ??
          firstItem.detail.value.eventTitle ??
          "")
        : "";

    return {
      artists: "",
      eventEndDateFull,
      eventOpenDateFull,
      eventOpenDay:
        eventOpenMillis === null
          ? ""
          : t(
              `common.string.dayOfWeek.${format(new Date(eventOpenMillis), "E")}`
            ),
      eventTitle,
      isLongEvent: eventOpenDateFull !== eventEndDateFull,
      products: items.map((item) => ({
        count: item.quantity,
        name: item.name,
        price_jpy: item.price,
        productType: toProductType(item.productType),
        subClassName: getItemVariantName(item),
      })),
    };
  });
};

/** 購入履歴APIの注文から支払案内モーダルの表示値を組み立てる */
export const convertOrderToConfirmCvsPaymentProps = (
  input: Input
): ConfirmCvsPaymentOrderProps => {
  const { order, t } = input;
  const { payType, unpaidPaymentInfo } = order;

  return {
    cvsInfo: toCvsInfo(payType, unpaidPaymentInfo),
    errorInfo: unpaidPaymentInfo?.errorInfo ?? null,
    eventGroups: toEventGroups(order, t),
    items: toItems(order.items),
    paypalInfo: toPayPalInfo(payType, unpaidPaymentInfo),
    phoneInfo: toPhoneInfo(payType, unpaidPaymentInfo),
    serviceFee: order.serviceFee ?? 0,
    settlementFee: order.settlementFee ?? 0,
    shippingFee: order.shippingFee,
    status: toPaymentStatus(order.status),
  };
};
